import { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Block,
  BlockContainer,
  ButtonContainer,
  GeneralStatus,
  IconButton,
  Loading,
  Panel,
  PanelContent,
  PanelHeader,
  PrimaryButton,
  BackText
} from '../atoms';
import {
  ChangeCourseStatus,
  ChangeSubCourseStatus,
  ComplexTable,
  ComplexTableHead,
  ComplexTableRow,
  CreateCourseLink,
  DisplayTable,
  DisplayTableRow,
  EditCourse,
  EditSubCourseModal,
  NewSubCourseModal,
  SimpleTable,
  SimpleTableRow
} from '../molecules';
import { withAuthRedirect } from '../hocs';
import { APIEndPoints, Request } from '../../apis';
import { z } from 'zod';
import type { TCourse, TSubCourse } from '../../common';
import { handleClose, handleOpen } from '../../common';
import { PRODUCT_TYPES } from '../../common/utils/constants';

const ButtonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 35px;
  margin-bottom: 10px;
  align-items: center;
`;

const SubCourseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin-bottom: 0;
  }
`;

const SubCoursesList = styled(ComplexTable)`
  margin-top: 20px;
  max-height: 600px;
  overflow-y: auto;

  td {
    vertical-align: middle;
  }
`;

const StyledButton = styled.button`
  background-color: transparent;
  border: 0;

  &:hover {
    cursor: pointer;
  }
`;

type TCourseDetails = TCourse | null | false;

type TSubCourseDetails = TSubCourse | null;

const CoursePage = () => {
  const [courseDetails, setCourseDetails] = useState<TCourseDetails>(null);
  const { merchantId, courseId } = useParams();
  const [subCourseModalOpen, setSubCourseModalOpen] = useState(false);
  const [triggerUpdate, setTriggerUpdate] = useState(false);
  const [subCourseEditModalOpen, setSubCourseEditModalOpen] = useState(false);
  const [subCourseDetails, setSubCourseDetails] =
    useState<TSubCourseDetails>(null);
  const [subCourseStatusModalOpen, setSubCourseStatusModalOpen] =
    useState(false);

  useEffect(() => {
    setSubCourseDetails(null);
    setCourseDetails(null);
    const getCourse = async () => {
      const course = await Request.safeGet({
        path: `${APIEndPoints.courses}/${merchantId}/${courseId}/details?access_token`,
        schema: z.any()
      });
      if (course.type === 'right') {
        setCourseDetails(course.value);
      } else {
        setCourseDetails(false);
      }
    };

    getCourse();
  }, [courseId, merchantId, triggerUpdate]);

  const handleSubCourseEdit = (details: TSubCourseDetails) => {
    setSubCourseDetails(details);
    setSubCourseEditModalOpen(true);
  };

  const handleSubCourseStatus = (details: TSubCourseDetails) => {
    setSubCourseDetails(details);
    setSubCourseStatusModalOpen(true);
  };

  return (
    <Panel>
      <PanelHeader noBottomPadding>
        <div>
          <span className="h3">Course</span>
          {courseDetails && <h1>{courseDetails.polName}</h1>}
        </div>
        <ButtonHeader>
          {merchantId && (
            <NavLink to={`/account/${merchantId}/courses`}>
              <BackText text="Back to courses" />
            </NavLink>
          )}

          {courseDetails && (
            <ButtonContainer
              horizontalSpacing="5.5px"
              verticalSpacing="7px"
              justify="flex-end"
            >
              <EditCourse
                courseDetails={courseDetails}
                setTriggerUpdate={() => setTriggerUpdate(!triggerUpdate)}
              />
              {!courseDetails.url && (
                <CreateCourseLink
                  courseDetails={courseDetails}
                  setTriggerUpdate={() => setTriggerUpdate(!triggerUpdate)}
                />
              )}

              <ChangeCourseStatus
                id={courseDetails.id}
                status={courseDetails.status}
                setTriggerUpdate={() => setTriggerUpdate(!triggerUpdate)}
              />

              {/* To implement later */}
              {/* <PrimaryButton
                type="button"
                onClick={() => handleOpen(setCourseModalOpen)}
              >
                Clone
                <span className="material-symbols-rounded">file_copy</span>
              </PrimaryButton> */}
            </ButtonContainer>
          )}
        </ButtonHeader>
      </PanelHeader>
      <PanelContent>
        {courseDetails === null && <Loading />}
        {courseDetails === false && (
          <DisplayTable colWidths={['auto']} colWidthsMd={['auto']}>
            <DisplayTableRow
              type="div"
              cells={[
                {
                  contentType: 'error',
                  content: `No Course found with ID "${courseId}"`
                }
              ]}
            />
          </DisplayTable>
        )}
        {courseDetails && (
          <>
            <BlockContainer>
              <Block size="half">
                <h2>Course Details</h2>
                <SimpleTable>
                  <SimpleTableRow
                    title="Course ID"
                    contentType="text"
                    content={courseDetails.id}
                  />
                  <SimpleTableRow
                    title="Course Code"
                    contentType="text"
                    content={courseDetails.polCode}
                  />
                  <SimpleTableRow
                    title="Course Name"
                    contentType="text"
                    content={courseDetails.polName}
                  />
                  <SimpleTableRow
                    title="Merchant Id"
                    contentType="text"
                    content={courseDetails.merchantId}
                  />
                  {courseDetails.productType ? (
                    <SimpleTableRow
                      title="Product Type"
                      contentType="text"
                      content={courseDetails.productType}
                    />
                  ) : null}
                  <SimpleTableRow
                    title="Product ID"
                    contentType="text"
                    content={courseDetails.productId}
                  />
                  <SimpleTableRow
                    title="Course Amount"
                    contentType="currency"
                    content={courseDetails.cost}
                  />
                  <SimpleTableRow
                    title="Created Date"
                    contentType="date"
                    content={courseDetails.createdAt}
                  />
                  <SimpleTableRow
                    title="Last Updated"
                    contentType="date"
                    content={courseDetails.updatedAt}
                  />
                  <SimpleTableRow
                    title="Nationally Accredited"
                    contentType="check"
                    content={courseDetails.nationallyAccredited}
                  />
                </SimpleTable>
              </Block>
              <Block size="half">
                <h2>App and Portal Settings</h2>
                <SimpleTable>
                  <SimpleTableRow
                    title="Status"
                    contentType="text"
                    content={
                      <GeneralStatus status={courseDetails.status} small />
                    }
                  />
                  <SimpleTableRow
                    title="URL Encoding"
                    contentType="text"
                    breakAll
                    content={courseDetails.url || ''}
                  />
                  {courseDetails.packagePolName ? (
                    <SimpleTableRow
                      title="Package PA Course Name"
                      contentType="text"
                      content={courseDetails.packagePolName}
                    />
                  ) : null}
                  {courseDetails.productType ===
                  PRODUCT_TYPES.STUDY_NOW_PAY_LATER ? (
                    <SimpleTableRow
                      title="Package PA Course ID"
                      contentType="text"
                      content={courseDetails.packagePolId || 'None'}
                    />
                  ) : null}
                </SimpleTable>
              </Block>
              <Block size="full">
                <SubCourseHeader>
                  <h2>Sub Courses</h2>
                  <ButtonContainer horizontalSpacing="6px" verticalSpacing="0">
                    <PrimaryButton
                      onClick={() => handleOpen(setSubCourseModalOpen)}
                    >
                      New Sub Course
                      <span className="material-symbols-rounded">add</span>
                    </PrimaryButton>
                    <NewSubCourseModal
                      isModalOpen={subCourseModalOpen}
                      handleModalClose={() =>
                        handleClose(setSubCourseModalOpen)
                      }
                      polId={courseDetails.id}
                      setTriggerUpdate={() => setTriggerUpdate(!triggerUpdate)}
                    />
                  </ButtonContainer>
                </SubCourseHeader>

                {courseDetails.subCategories &&
                  courseDetails.subCategories.length > 0 && (
                    <SubCoursesList>
                      <ComplexTableHead
                        cols={[
                          {
                            name: 'Sub course name',
                            cellStyling: { width: 'auto' }
                          },
                          {
                            name: 'Course Amount',
                            cellStyling: { width: '240px' }
                          },
                          {
                            name: 'Status',
                            cellStyling: { width: '10rem' }
                          },
                          {
                            name: '',
                            cellStyling: { width: '100px' }
                          }
                        ]}
                      />
                      <tbody>
                        {courseDetails.subCategories.map(
                          ({ id, name, cost, status }, index) => (
                            <ComplexTableRow
                              key={id}
                              data-index={index}
                              cells={[
                                {
                                  contentType: 'text',
                                  content: name
                                },
                                {
                                  contentType: 'currency',
                                  content: cost
                                },
                                {
                                  contentType: 'text',
                                  content: (
                                    <GeneralStatus status={status} small />
                                  )
                                },
                                {
                                  contentType: 'text',
                                  content: (
                                    <ButtonContainer
                                      horizontalSpacing="6px"
                                      verticalSpacing="0"
                                      justify="flex-end"
                                    >
                                      <IconButton
                                        iconType="Edit"
                                        onClick={() => {
                                          handleSubCourseEdit({
                                            id,
                                            name,
                                            cost,
                                            status
                                          });
                                        }}
                                      />
                                      <IconButton
                                        iconType={
                                          status === 'ACTIVE'
                                            ? 'Deactivate'
                                            : 'Activate'
                                        }
                                        onClick={() => {
                                          handleSubCourseStatus({
                                            id,
                                            name,
                                            cost,
                                            status
                                          });
                                        }}
                                      />
                                    </ButtonContainer>
                                  )
                                }
                              ]}
                            />
                          )
                        )}
                      </tbody>
                      {subCourseDetails && (
                        <>
                          <EditSubCourseModal
                            isModalOpen={subCourseEditModalOpen}
                            handleModalClose={() =>
                              handleClose(setSubCourseEditModalOpen)
                            }
                            polId={courseDetails.id}
                            details={subCourseDetails}
                            setTriggerUpdate={() =>
                              setTriggerUpdate(!triggerUpdate)
                            }
                          />
                          <ChangeSubCourseStatus
                            isModalOpen={subCourseStatusModalOpen}
                            handleModalClose={() =>
                              handleClose(setSubCourseStatusModalOpen)
                            }
                            id={courseDetails.id}
                            details={subCourseDetails}
                            setTriggerUpdate={() =>
                              setTriggerUpdate(!triggerUpdate)
                            }
                          />
                        </>
                      )}
                    </SubCoursesList>
                  )}
              </Block>
            </BlockContainer>
          </>
        )}
      </PanelContent>
    </Panel>
  );
};

const SecureCoursePage = withAuthRedirect(CoursePage);
export { SecureCoursePage as CoursePage };
